import { ProjectTypes } from '@platform/types';
import { Map, RequestParameters, ResourceType, StyleSpecification } from 'maplibre-gl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import MapLibreMap from './Map';
import { geoBufferTilesRequests, mapTilerTilesRequests, nonTileRequests } from './mapRequestTransformations';
import { useMapsContext } from './MapsContext';
import { getMapStyle } from './utils';

interface MapProps {
  mapObj: ProjectTypes.MapObject;
  children?: React.ReactNode;
  withFooter?: boolean;
  onMount?: (mapRef: Map) => void;
}

const MapsContextMap: React.FC<MapProps> = ({ children, withFooter = true, mapObj, onMount }) => {
  const { apis, jwtToken, onUnMountMap, onMountMap, useMapHighlight, activeMap, mapClickHandler } = useMapsContext();
  const mapRef = useRef<Map | null>(null);
  const [mapStyle, setMapStyle] = useState<StyleSpecification>();
  const { id, styleSpec } = mapObj;
  const { baseMapId, workingLayers } = styleSpec;

  const baseMapStyleQuery = useQuery(['base-maps', baseMapId], () => apis.baseMaps.get(styleSpec.baseMapId));
  const basemapStyle = baseMapStyleQuery.data;

  useEffect(() => {
    if (!basemapStyle) return;

    const newStyle: StyleSpecification = JSON.parse(JSON.stringify(mapRef.current?.getStyle() || basemapStyle));
    newStyle.layers = newStyle.layers.filter((l) => !l.id.startsWith('STYLE-SPEC:'));

    const generatedStyle = getMapStyle(styleSpec);

    newStyle.sources = { ...newStyle.sources, ...generatedStyle.sources };

    // Find the index of the first symbol layer in the map style
    let firstSymbolId = '';
    for (const item of basemapStyle.layers) {
      if (item.type === 'symbol') {
        firstSymbolId = item.id;
        break;
      }
    }

    const firstSymbolLayerIndex = firstSymbolId
      ? newStyle.layers.findIndex((x) => x.id === firstSymbolId)
      : newStyle.layers.length;

    newStyle.layers.splice(firstSymbolLayerIndex, 0, ...generatedStyle.layers);

    //   console.log(newStyle);
    setMapStyle(newStyle);
  }, [styleSpec, basemapStyle]);

  const mapTilesRequestTransformation = useCallback(
    (url: string, resourceType?: ResourceType): RequestParameters => {
      let transformedUrl;

      // check for non tile requests
      transformedUrl = nonTileRequests(url, resourceType);
      if (transformedUrl) return { url: transformedUrl };

      // check for map tiler requests
      transformedUrl = mapTilerTilesRequests(url);
      if (transformedUrl) return { url: transformedUrl, headers: { Authorization: `Bearer ${jwtToken}` } };

      // handling GeoBuffer tile requests (social explorer layers and user uploaded layers)
      // in order to display GeoBuffer (tiles3) tiles on MapLibre map we need to rewrite tile requests by adding layers and columns to the request
      transformedUrl = geoBufferTilesRequests(url, styleSpec /*, sourceLayer, defaultSourceLayer*/);
      return {
        url: transformedUrl,
      };
    },
    [workingLayers]
  );

  if (!mapStyle)
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 text-xl text-gray-400">
        Loading Map
      </div>
    );

  return (
    <div
      className="relative h-full w-full"
      onClick={() => mapClickHandler?.(mapObj)}
      onKeyDown={() => mapClickHandler?.(mapObj)}
    >
      <MapLibreMap
        style={mapStyle ?? ''}
        customTransformRequest={mapTilesRequestTransformation}
        zoom={styleSpec.zoom}
        center={styleSpec.center}
        onMount={(mapElementRef) => {
          mapRef.current = mapElementRef;
          onMountMap?.(id, mapElementRef);
          onMount?.(mapElementRef);
        }}
        onUnmount={() => {
          mapRef.current = null;
          onUnMountMap?.(id);
        }}
      />
      {children && <div className="pointer-events-none absolute top-0 left-0 right-0 bottom-0 z-10">{children}</div>}
      {withFooter && (
        <div className="z-1 absolute bottom-0 right-0 flex justify-between rounded-tl-md bg-white bg-opacity-75 px-4 py-0.5">
          <div />
          <div className="text-xs">{`Zoom: ${Math.round(styleSpec.zoom * 100) / 100}`}</div>
        </div>
      )}
      {useMapHighlight && activeMap?.id === mapObj.id && (
        <div className="border-primary-400 pointer-events-none absolute top-0 left-0 right-0 bottom-0 border-4 opacity-60" />
      )}
    </div>
  );
};

export default MapsContextMap;
